/**
 * `entities/taxonomy-dataset` query options factories.
 *
 * Calls the hey-api client directly by URL, matching the four sibling taxonomy
 * slices. `assetsHttp` injects the `x-selected-workspace-id` header, so
 * workspace context is implicit.
 *
 * A generated `TaxonomyDatasetsService` does now exist in the assets-api client,
 * and switching the taxonomy slices onto it would remove these hand-written
 * mirrors — but that is a migration for all five at once, not something to do
 * to one of them here.
 */
import { queryOptions } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/http-client';
import type { TaxonomyDataset, TaxonomyRecordPage } from '../model/types';
import { TAXONOMY_DATASET_QUERY_KEYS } from './query-keys';

const DATASETS_URL = '/api/v1/taxonomy-datasets';

export const taxonomyDatasetsQueryOptions = () =>
  queryOptions({
    queryKey: TAXONOMY_DATASET_QUERY_KEYS.list(),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({ throwOnError: true, url: DATASETS_URL, signal });
      return (response.data as TaxonomyDataset[] | undefined) ?? [];
    },
  });

export const taxonomyDatasetDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_DATASET_QUERY_KEYS.detail(id),
    queryFn: async ({ signal }) => {
      const response = await assetsHttp.get({
        throwOnError: true,
        url: `${DATASETS_URL}/{id}`,
        path: { id },
        signal,
      });
      if (!response.data) {
        throw new Error('Failed to load taxonomy');
      }
      return response.data as TaxonomyDataset;
    },
  });

/**
 * The grid's records.
 *
 * Deliberately fetches the whole dataset rather than paging on scroll: the grid
 * needs every row in memory anyway to assemble previews, detect duplicate names
 * and save a batch. The server pages so one response cannot be unbounded, so
 * this walks the cursor to exhaustion — and the record cap is what keeps that
 * walk finite.
 */
export const taxonomyRecordsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: TAXONOMY_DATASET_QUERY_KEYS.records(id),
    queryFn: async ({ signal }) => {
      const all: TaxonomyRecordPage['data'] = [];
      let cursor: string | null = null;

      do {
        const response = await assetsHttp.get({
          throwOnError: true,
          url: `${DATASETS_URL}/{id}/records`,
          path: { id },
          query: { pageSize: 1000, ...(cursor ? { cursor } : {}) },
          signal,
        });
        const page = response.data as TaxonomyRecordPage | undefined;
        if (!page) {
          break;
        }
        all.push(...page.data);
        cursor = page.hasMore ? page.nextCursor : null;
      } while (cursor);

      return all;
    },
  });
